/**
 * Gymir — Tarjouspyyntökori (Quote Request Cart)
 * 
 * Handles: localStorage cart, customer metafield sync via PHP backend,
 * session token auth, cart UI, form submission → Draft Order
 */

(function() {
  'use strict';

  // ============================================================
  // CONFIG
  // ============================================================
  const CONFIG = {
    apiBase: 'https://kmuonio-sync.fi/gmr/quote-api',
    storageKey: 'gymir_quote_cart',
    sessionStorageKey: 'gymir_session_token',
    vatRate: 0.255, // ALV 25.5%
  };

  // ============================================================
  // SESSION TOKEN
  // ============================================================
  let sessionToken = null;

  function getStoredToken() {
    try {
      const data = JSON.parse(sessionStorage.getItem(CONFIG.sessionStorageKey));
      if (data && data.token && data.expires > Date.now()) {
        return data.token;
      }
      sessionStorage.removeItem(CONFIG.sessionStorageKey);
    } catch {}
    return null;
  }

  function storeToken(token) {
    sessionStorage.setItem(CONFIG.sessionStorageKey, JSON.stringify({
      token: token,
      expires: Date.now() + 23 * 60 * 60 * 1000, // 23h (safety buffer from 24h server TTL)
    }));
    sessionToken = token;
  }

  async function authenticate() {
    const qc = window.__quoteCart || {};
    if (!qc.customerId || qc.customerId <= 0) return;

    // Check stored token first
    const stored = getStoredToken();
    if (stored) {
      sessionToken = stored;
      return;
    }

    // Request new token
    try {
      const resp = await fetch(CONFIG.apiBase + '/auth.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: qc.customerId,
          email: qc.customerEmail,
        }),
      });
      const result = await resp.json();
      if (result.success && result.token) {
        storeToken(result.token);
      }
    } catch (e) {
      console.warn('Quote cart auth failed:', e.message);
    }
  }

  // ============================================================
  // API CALL (direct to PHP backend with session token)
  // ============================================================
  async function apiCall(endpoint, data, _retried) {
    const headers = { 'Content-Type': 'application/json' };
    if (sessionToken) {
      headers['X-Session-Token'] = sessionToken;
    }

    const resp = await fetch(CONFIG.apiBase + '/' + endpoint, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(data),
    });
    
    // 401 = token expired → re-authenticate once and retry
    if (resp.status === 401 && !_retried && sessionToken) {
      sessionToken = null;
      sessionStorage.removeItem(CONFIG.sessionStorageKey);
      await authenticate();
      if (sessionToken) {
        return apiCall(endpoint, data, true);
      }
    }

    const result = await resp.json();
    if (!resp.ok || !result.success) {
      throw new Error(result.error || 'API error');
    }
    return result;
  }

  // ============================================================
  // CART STORAGE (localStorage)
  // ============================================================
  function getLocalCart() {
    try {
      return JSON.parse(localStorage.getItem(CONFIG.storageKey)) || [];
    } catch { return []; }
  }

  function saveLocalCart(items) {
    localStorage.setItem(CONFIG.storageKey, JSON.stringify(items));
    updateBadge(items.length);
  }

  function clearLocalCart() {
    localStorage.removeItem(CONFIG.storageKey);
    updateBadge(0);
  }

  // ============================================================
  // CART OPERATIONS
  // ============================================================
  function addToCart(product) {
    const cart = getLocalCart();
    const existing = cart.find(i => i.variant_id === product.variant_id);
    
    if (existing) {
      existing.quantity = Math.min(existing.quantity + 1, 100);
    } else {
      cart.push({
        product_id: product.product_id,
        variant_id: product.variant_id,
        title: product.title,
        variant_title: product.variant_title || '',
        image: product.image,
        price: product.price,
        quantity: 1,
      });
    }
    
    saveLocalCart(cart);
    syncToServer(cart);
    return cart;
  }

  function updateQuantity(variantId, qty) {
    const cart = getLocalCart();
    const item = cart.find(i => i.variant_id === variantId);
    if (item) {
      item.quantity = Math.max(1, Math.min(qty, 100));
    }
    saveLocalCart(cart);
    syncToServer(cart);
    return cart;
  }

  function removeFromCart(variantId) {
    let cart = getLocalCart();
    cart = cart.filter(i => i.variant_id !== variantId);
    saveLocalCart(cart);
    syncToServer(cart);
    return cart;
  }

  function clearCart() {
    clearLocalCart();
    syncToServer([]);
    return [];
  }

  // ============================================================
  // SERVER SYNC (customer metafield via session token)
  // ============================================================
  async function syncToServer(items) {
    if (!sessionToken) return;
    try {
      await apiCall('save-cart.php', { items });
    } catch (e) {
      console.warn('Quote cart sync failed:', e.message);
    }
  }

  async function loadFromServer() {
    if (!sessionToken) return null;
    try {
      const result = await apiCall('load-cart.php', {});
      return result.items || [];
    } catch (e) {
      console.warn('Quote cart load failed:', e.message);
      return null;
    }
  }

  async function mergeServerCart() {
    if (!sessionToken) return;
    
    const serverItems = await loadFromServer();
    if (!serverItems) return;
    
    const localItems = getLocalCart();
    
    // Server is always master
    if (localItems.length === 0) {
      // No local items → just use server
      saveLocalCart(serverItems);
      return;
    }
    
    // Find local items that don't exist on server (added while logged out)
    const newFromLocal = localItems.filter(
      local => !serverItems.find(s => s.variant_id === local.variant_id)
    );
    
    if (newFromLocal.length > 0) {
      // Add local-only items to server cart
      const merged = [...serverItems, ...newFromLocal];
      saveLocalCart(merged);
      syncToServer(merged);
      showToast('Aiemmin lisäämäsi tuotteet on yhdistetty tarjouspyyntökoriin');
    } else {
      // Server has everything → use server as-is
      saveLocalCart(serverItems);
    }
    
    // From now on, server is the source of truth
    // localStorage is just a local cache of server state
  }

  function showToast(message) {
    const existing = document.getElementById('quote-toast');
    if (existing) existing.remove();
    
    const toast = document.createElement('div');
    toast.id = 'quote-toast';
    toast.textContent = message;
    toast.style.cssText = 'position:fixed;bottom:20px;left:50%;transform:translateX(-50%);background:#333;color:#fff;padding:12px 24px;border-radius:8px;font-size:14px;z-index:99999;opacity:0;transition:opacity 0.3s;';
    document.body.appendChild(toast);
    
    requestAnimationFrame(() => { toast.style.opacity = '1'; });
    setTimeout(() => {
      toast.style.opacity = '0';
      setTimeout(() => toast.remove(), 300);
    }, 5000);
  }

  // ============================================================
  // BADGE
  // ============================================================
  function updateBadge(count) {
    const badge = document.getElementById('quote-cart-badge');
    if (!badge) return;
    
    if (count > 0) {
      badge.textContent = count;
      badge.style.display = 'flex';
    } else {
      badge.style.display = 'none';
    }
  }

  // ============================================================
  // PRODUCT PAGE — "Lisää tarjouspyyntöön" button
  // ============================================================
  function initProductButton() {
    // Support multiple buttons (product page + quick add modals)
    document.querySelectorAll('.quote-cart-add-btn').forEach(function(btn) {
      if (btn.dataset.quoteListenerAttached) return;
      btn.dataset.quoteListenerAttached = '1';
      
      const wrapper = btn.closest('.quote-cart-button-wrapper');
      if (!wrapper) return;
      
      btn.addEventListener('click', function() {
        let variantId = parseInt(wrapper.dataset.variantId);
        let price = parseFloat(wrapper.dataset.productPrice);
        let variantTitle = '';
        
        // Find variant selector — search product form first, then page-level
        const productForm = wrapper.closest('form[action*="/cart/add"]') 
          || document.querySelector('form[action*="/cart/add"]');
        const variantInput = (productForm && productForm.querySelector('select[name="id"], input[name="id"]'))
          || document.querySelector('select[name="id"], input[name="id"]');
        
        if (variantInput && variantInput.value) {
          variantId = parseInt(variantInput.value);
          // Get variant title from select option text
          if (variantInput.tagName === 'SELECT' && variantInput.selectedOptions[0]) {
            variantTitle = variantInput.selectedOptions[0].textContent.trim();
          }
        }
        
        // Try to get variant title from Dawn's variant radios/buttons/selects
        if (!variantTitle) {
          const selectedOptions = [];
          document.querySelectorAll('variant-selects select, variant-radios fieldset input:checked, .product-form__input input[type="radio"]:checked').forEach(function(el) {
            if (el.tagName === 'SELECT') {
              selectedOptions.push(el.selectedOptions[0] ? el.selectedOptions[0].textContent.trim() : '');
            } else {
              selectedOptions.push(el.value);
            }
          });
          if (selectedOptions.length > 0) {
            variantTitle = selectedOptions.filter(Boolean).join(' / ');
          }
        }

        const product = {
          product_id: parseInt(wrapper.dataset.productId),
          variant_id: variantId,
          title: wrapper.dataset.productTitle,
          variant_title: variantTitle,
          image: wrapper.dataset.productImage,
          price: price,
        };
        
        addToCart(product);
        
        const msg = wrapper.querySelector('.quote-cart-added-msg');
        if (msg) {
          msg.style.display = 'block';
          setTimeout(() => { msg.style.display = 'none'; }, 3000);
        }
        
        btn.disabled = true;
        setTimeout(() => { btn.disabled = false; }, 1000);
      });
    });
    
    // Re-run when DOM changes (Quick Add modals inject content dynamically)
    if (!window.__quoteCartObserver) {
      window.__quoteCartObserver = new MutationObserver(function() {
        initProductButton();
      });
      window.__quoteCartObserver.observe(document.body, { childList: true, subtree: true });
    }
  }

  // ============================================================
  // CART PAGE
  // ============================================================
  function initCartPage() {
    const page = document.getElementById('quote-cart-page');
    if (!page) return;

    const loading = document.getElementById('quote-cart-loading');
    const empty = document.getElementById('quote-cart-empty');
    const content = document.getElementById('quote-cart-content');
    const success = document.getElementById('quote-cart-success');

    function formatPrice(cents) {
      return cents.toFixed(2).replace('.', ',') + ' €';
    }

    function renderCart() {
      const cart = getLocalCart();
      
      loading.style.display = 'none';
      
      if (cart.length === 0) {
        empty.style.display = 'block';
        content.style.display = 'none';
        return;
      }
      
      empty.style.display = 'none';
      content.style.display = 'block';
      
      const tbody = document.getElementById('quote-cart-tbody');
      tbody.innerHTML = '';
      
      let subtotal = 0;
      
      cart.forEach(item => {
        const itemTotal = item.price * item.quantity;
        subtotal += itemTotal;
        
        const tr = document.createElement('tr');
        const variantLabel = item.variant_title ? `<span class="quote-item-variant">${escapeHtml(item.variant_title)}</span>` : '';
        tr.innerHTML = `
          <td><img src="${item.image || ''}" alt="" class="quote-item-img" loading="lazy"></td>
          <td><span class="quote-item-title">${escapeHtml(item.title)}</span>${variantLabel}</td>
          <td><input type="number" class="quote-item-qty" value="${item.quantity}" min="1" max="100" data-variant="${item.variant_id}"></td>
          <td><button type="button" class="quote-item-remove" data-variant="${item.variant_id}" aria-label="Poista">&times;</button></td>
        `;
        tbody.appendChild(tr);
      });
      
      // Update item count (no prices shown — quote request)
      const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);
      const countEl = document.getElementById('quote-item-count');
      if (countEl) countEl.textContent = totalItems + ' kpl';
      
      tbody.querySelectorAll('.quote-item-qty').forEach(input => {
        input.addEventListener('change', function() {
          updateQuantity(parseInt(this.dataset.variant), parseInt(this.value));
          renderCart();
        });
      });
      
      tbody.querySelectorAll('.quote-item-remove').forEach(btn => {
        btn.addEventListener('click', function() {
          removeFromCart(parseInt(this.dataset.variant));
          renderCart();
        });
      });
    }

    function prefillForm() {
      const qc = window.__quoteCart || {};
      if (qc.customerFirstName) document.getElementById('quote-firstname').value = qc.customerFirstName;
      if (qc.customerLastName) document.getElementById('quote-lastname').value = qc.customerLastName;
      if (qc.customerEmail) document.getElementById('quote-email').value = qc.customerEmail;
      if (qc.customerPhone) document.getElementById('quote-phone').value = qc.customerPhone;
      if (qc.customerZip) document.getElementById('quote-zip').value = qc.customerZip;
    }

    const form = document.getElementById('quote-cart-form');
    form.addEventListener('submit', async function(e) {
      e.preventDefault();
      
      const msgEl = document.getElementById('quote-form-message');
      msgEl.style.display = 'none';
      
      const fields = {
        first_name: document.getElementById('quote-firstname').value.trim(),
        last_name: document.getElementById('quote-lastname').value.trim(),
        email: document.getElementById('quote-email').value.trim(),
        phone: document.getElementById('quote-phone').value.trim(),
        postal_code: document.getElementById('quote-zip').value.trim(),
        notes: document.getElementById('quote-notes').value.trim(),
      };
      
      form.querySelectorAll('.quote-field-error').forEach(el => el.classList.remove('quote-field-error'));
      
      let hasError = false;
      ['first_name', 'last_name', 'email', 'phone', 'postal_code'].forEach(key => {
        const inputId = {
          first_name: 'quote-firstname',
          last_name: 'quote-lastname',
          email: 'quote-email',
          phone: 'quote-phone',
          postal_code: 'quote-zip',
        }[key];
        if (!fields[key]) {
          document.getElementById(inputId).classList.add('quote-field-error');
          hasError = true;
        }
      });
      
      if (fields.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email)) {
        document.getElementById('quote-email').classList.add('quote-field-error');
        hasError = true;
      }
      
      if (hasError) {
        msgEl.className = 'quote-form-message quote-msg-error';
        msgEl.textContent = 'Täytä kaikki pakolliset kentät.';
        msgEl.style.display = 'block';
        return;
      }
      
      const cart = getLocalCart();
      if (cart.length === 0) {
        msgEl.className = 'quote-form-message quote-msg-error';
        msgEl.textContent = 'Korisi on tyhjä.';
        msgEl.style.display = 'block';
        return;
      }
      
      const submitBtn = document.getElementById('quote-submit-btn');
      submitBtn.disabled = true;
      submitBtn.textContent = 'Lähetetään...';
      
      try {
        const result = await apiCall('submit-quote.php', {
          items: cart,
          contact: fields,
        });
        
        clearLocalCart();
        content.style.display = 'none';
        success.style.display = 'block';
        
        if (result.draft_order_name) {
          document.getElementById('quote-success-ref').textContent = 
            'Viite: ' + result.draft_order_name;
        }
        
      } catch (err) {
        msgEl.className = 'quote-form-message quote-msg-error';
        msgEl.textContent = err.message === 'Rate limit exceeded' 
          ? 'Liian monta pyyntöä. Yritä myöhemmin uudelleen.'
          : 'Virhe tarjouspyynnön lähettämisessä. Yritä uudelleen.';
        msgEl.style.display = 'block';
      } finally {
        submitBtn.disabled = false;
        submitBtn.textContent = 'LÄHETÄ TARJOUSPYYNTÖ';
      }
    });

    document.getElementById('quote-clear-btn').addEventListener('click', function() {
      if (confirm('Haluatko varmasti tyhjentää tarjouspyyntökorin?')) {
        clearCart();
        renderCart();
      }
    });

    prefillForm();
    
    mergeServerCart().then(() => {
      renderCart();
    }).catch(() => {
      renderCart();
    });
  }

  // ============================================================
  // HELPERS
  // ============================================================
  function escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  // ============================================================
  // INIT
  // ============================================================
  async function init() {
    updateBadge(getLocalCart().length);

    // Authenticate if customer is logged in
    await authenticate();

    // Merge server cart once per session (not every page load)
    if (sessionToken && !sessionStorage.getItem('quote_merged')) {
      await mergeServerCart();
      updateBadge(getLocalCart().length);
      sessionStorage.setItem('quote_merged', '1');
    }

    initProductButton();
    initCartPage();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
